import { useMemo, type ReactElement, useCallback } from 'react'
import { Responsive, WidthProvider } from 'react-grid-layout'

import type { ViewProps } from '../common-props'
import type { IBuilderConfig } from '~/types/graph-builder'

import ContentLayout from '~/components/ContentLayout'
import DynamicGraphBlock from '~/components/DynamicGraphBlock'
import Layout from '~/components/Layouts/Layout'
import Section from '~/components/Section'
import { COMMON_GRID_PROPS } from '~/const'
import { useAppSummary } from '~/providers/AppSummary'
import { useChartConfigsListQuery } from '~/queries/graphs-builder'
import { CA_PAGES, CA_SECTIONS } from '~/types/graph-builder'
import { getGraphLayoutPosition } from '~/utils/grid'
import { capitalize } from '~/utils/string'

const ResponsiveGridLayout = WidthProvider(Responsive)

const PAGE = CA_PAGES.SANDBOX

const Sandbox = ({ analyticsApiFn, eventsData, isAppLoading, kpiField }: ViewProps): ReactElement => {
  const { appKey, deviceType } = useAppSummary()

  /** Queries & Mutations */

  const { data: chartConfigs, isLoading } = useChartConfigsListQuery({
    appKey,
    deviceType,
    pageName: PAGE,
  })

  const formattedConfigs: IBuilderConfig[] = useMemo(
    () =>
      chartConfigs?.data?.map(
        ({
          applications,
          force_kpi_field,
          how,
          mode,
          nb_last_days_to_ignore,
          page,
          position,
          related_glossary_items,
          required_breakdown,
          section,
          series,
          title,
          what,
          y_axes,
        }) => ({
          applications: applications.map(({ app_key }) => app_key) || [],
          forceKPIField: force_kpi_field,
          mode,
          nbLastDaysToIgnore: nb_last_days_to_ignore,
          page,
          position,
          requiredBreakdown: required_breakdown,
          section,
          series: series.map((el) => ({
            defaultBreakdown: el.default_breakdown,
            graphType: el.graph_type,
            minNbu: el.min_nbu,
            normalizedBy: el.normalized_by,
            values: el.values,
            withInf: el.with_inf,
            withSup: el.with_sup,
            withTrend: el.with_trend,
            yAxisName: el.y_axes_name,
          })),
          subsection: '',
          title,
          tooltip: {
            how,
            relatedGlossaryItems: related_glossary_items,
            what,
          },
          yAxes: y_axes || [],
        })
      ) || [],
    [chartConfigs?.data]
  )

  const graphOptions = useCallback(
    (config: IBuilderConfig) => ({
      apiSelectionFn: analyticsApiFn,
      appKey,
      config,
      deviceType,
      eventsData,
      isAppLoading,
      kpiField,
    }),
    [analyticsApiFn, appKey, deviceType, eventsData, isAppLoading, kpiField]
  )

  /** Render */

  return (
    <Layout className="lookers-sandbox-layout" withMargin>
      <ContentLayout loading={isLoading}>
        <Section title="">
          <ResponsiveGridLayout {...COMMON_GRID_PROPS} compactType={null}>
            {formattedConfigs
              .filter(({ section }) => !section)
              .map((config, idx) => (
                <div data-grid={getGraphLayoutPosition(`section-1-${idx}`, idx)} key={idx}>
                  <DynamicGraphBlock {...graphOptions(config)} />
                </div>
              ))}
          </ResponsiveGridLayout>
        </Section>
        {Object.values(CA_SECTIONS[PAGE]).map((section) => (
          <Section key={section} title={capitalize(section)}>
            <ResponsiveGridLayout {...COMMON_GRID_PROPS} compactType={null}>
              {formattedConfigs
                .filter(({ section: s }) => s === section)
                .map((config, idx) => (
                  <div data-grid={getGraphLayoutPosition(`section-2-${idx}`, idx)} key={idx}>
                    <DynamicGraphBlock {...graphOptions(config)} />
                  </div>
                ))}
            </ResponsiveGridLayout>
          </Section>
        ))}
      </ContentLayout>
    </Layout>
  )
}

export default Sandbox
